import { Component, OnInit, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map, startWith } from "rxjs";
import { NonNullableFormBuilder, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { AuthService } from "../auth.service";
import { NgxSpinnerService } from "ngx-spinner";

import { Bind } from "primeng/bind";
import { InputText } from "primeng/inputtext";
import { ButtonDirective } from "primeng/button";

@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.css"],
    imports: [RouterLink, FormsModule, ReactiveFormsModule, Bind, InputText, ButtonDirective]
})
export class LoginComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private router = inject(Router);
  private authService = inject(AuthService);
  private spinner = inject(NgxSpinnerService);

  loginForm = this.fb.group({
    username: ["", Validators.required],
    password: ["", Validators.required],
  });
  formValid = toSignal(
    this.loginForm.statusChanges.pipe(
      map((status) => status === "VALID"),
      startWith(this.loginForm.valid)
    ),
    { requireSync: true }
  );

  submitted = signal(false);
  error = signal<any>(null);
  loginError = signal("");
  decodedUser: any;

  ngOnInit() {
    // ✅ Check if a currentUser exists
    const currentUser = localStorage.getItem("currentUser");

    if (currentUser) {
      const token = JSON.parse(currentUser)?.token;

      // ✅ If there's a token, check validity via AuthService
      if (token) {
        const isValid = this.authService.isTokenValid(token);

        if (!isValid) {
          console.log("Removing expired/invalid token from localStorage");
          localStorage.removeItem("currentUser");
        } else {
          console.log("Valid token found — keeping currentUser");
        }
      } else {
        console.log("No token found in currentUser — removing it");
        localStorage.removeItem("currentUser");
      }
    }

    this.spinner.hide();
  }

  get username() {
    return this.loginForm.controls.username;
  }

  get password() {
    return this.loginForm.controls.password;
  }

  onSubmit() {
    this.submitted.set(true);

    if (this.loginForm.invalid) return;

    const { username, password } = this.loginForm.getRawValue();
    this.authService.login(username, password).subscribe({
      next: () => {
        if (this.authService.isLoggedIn()) {
          this.decodedUser = this.authService.getDecodedToken();

          const redirect = this.authService.redirectUrl || "/admin";
          this.router.navigateByUrl(redirect).then((success) => {
            if (!success) {
              this.loginError.set("Failed to navigate after login.");
            }
          });
        } else {
          this.loginError.set("Username or password is incorrect.");
        }
      },
      error: (error) => {
        this.error.set(error);
        this.loginForm.reset();

        if (error.error?.message) {
          this.loginError.set(error.error.message);
        } else if (error.status === 0) {
          this.loginError.set("Cannot connect to server. Please try again later.");
        } else {
          this.loginError.set("An unexpected error occurred.");
        }
      },
    });
  }
}
